<script lang="ts" setup>
	import useNotificationApi from '~/services/apis/notifications'
	import type { ModalOption } from '~/components/Dialogs/options.vue'

	const { getTodoList } = useNotificationApi()
	const { productCategoryType, toLower } = useUtils()
	const { timeDifference } = useRelativeTime()
	// @ts-ignore
	const { todoRecords: todoLists } = reactor(useStore())
	const { isLoading } = useApiLoader(async () => await getTodoList<NotificationTodo[]>())
	const highlightedTodo = shallowRef<{
		category: string
		planName: string
		purchaseId: string
		categoryName?: string
	} | null>({
		category: '',
		planName: '',
		purchaseId: ''
	})
	const categoryType = computed(() =>
		productCategoryType(highlightedTodo.value?.category, highlightedTodo.value?.planName)
	)

	const isCustodianOptionOpen = shallowRef(false)
	const dialogOptions = computed<{
		title: string
		label: string
		data: ModalOption[]
	}>(() => {
		const customerType = categoryType.value?.isAuto
			? 'Policy Holders'
			: categoryType.value.isHealth || categoryType.value?.isTravel
				? 'Staff'
				: highlightedTodo.value?.category
		const title = `Upload ${customerType} to plan`
		const dialogIcon = {
			excel: {
				dir: 'documents',
				name: 'excel-sm',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			person: {
				dir: 'fill',
				name: 'person-remove',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			document: {
				dir: 'documents',
				name: 'export',
				wrapperClass: 'bg-success-50 border-success-100'
			}
		}

		return {
			title,
			label: `Select a method to add ${customerType} to your plan`,
			data: [
				{
					desc: `Upload list of ${customerType} to add via an excel sheet`,
					key: 'batch',
					name: 'Batch Method',
					icon: dialogIcon.excel
				},
				{
					desc: `Add ${customerType} via manual method `,
					key: 'manual',
					name: 'Manual Method',
					icon:
						categoryType.value.isHealth || categoryType.value?.isTravel
							? dialogIcon.person
							: dialogIcon.document
				}
			]
		}
	})

	const TodoProperties: Record<string, Omit<TodosRecord[string], 'callback'>> = {
		activation: {
			bg: 'bg-warning-50',
			textColor: 'text-warning-600',
			priority: 'quite',
			icon: {
				dir: 'fill',
				name: 'people'
			}
		},
		adding: {
			bg: 'bg-error-50',
			textColor: 'text-error-600',
			priority: 'high',
			icon: {
				name: 'alarm',
				dir: 'media',
				pathClass: 'fill-error-500'
			}
		}
	}
	const todos = computed(() => {
		const records = todoLists.value.map(todo => {
			let properties: Record<string, unknown> = { ...todo }
			if (todo?.title?.includes('activation')) {
				properties = {
					...properties,
					label: todo?.text,
					...TodoProperties.activation,
					callback(arg: unknown) {
						const response = arg as NotificationTodo
						if (response.id) {
							navigateTo({
								path: `/manage-custodian/${response.purchase_id}/activate`
							})
						}
					}
				}
			} else if (todo?.title?.includes('add')) {
				properties = {
					...properties,
					...TodoProperties.adding,
					callback: (arg: unknown) => {
						const response = arg as NotificationTodo
						if (response.meta.id) {
							if (response.meta?.product?.name) {
								const id = response?.purchase_id || response?.meta?.purchase_id
								// Get the category name of the plan in order to route the user on which page to open
								const tabType = response.meta.product.productCategory.name
								highlightedTodo.value = {
									planName: response.meta.product.name,
									category: response.meta?.product?.productCategory?.name || '',
									purchaseId: id as string,
									categoryName: tabType
								}
								isCustodianOptionOpen.value = true
							}
						}
					}
				}
			}
			return properties
		}) as (TodosRecord[string] & NotificationTodo)[]
		return records
	})

	const processTodo = (arg: ModalOption) => {
		const planN = highlightedTodo.value?.planName?.split(' ')[0]
		const planId = highlightedTodo.value?.purchaseId || ''
		const planCategory = highlightedTodo.value?.categoryName?.toLowerCase()

		let path = ''
		path = `/manage-custodian/${planId || ''}/add/new`

		if (planN && planCategory) {
			isCustodianOptionOpen.value = false
			navigateTo({
				path,
				query: {
					method: arg.key as string
				}
			})
			return null
		}
		useNotification().alert({
			status: 400,
			message: 'Something went wrong, please try again later.'
		})
	}
</script>

<template>
	<div>
		<template v-if="isLoading">
			<a-skeleton v-for="item in 3" :key="'todo-notification-' + item" avatar active loading class="mb-5" />
		</template>
		<shared-empty-state
			v-else-if="!todoLists?.length"
			title="No Pending To-Dos"
			description="You Key to-dos will appear here when you have any"
			class="mt-10 space-y-2"
			title-class="font-semibold  text-gray-600"
			desc-class="px-5 max-w-[356px]"
		>
			<template #icon>
				<div class="py-5">
					<lazy-tools-icon icon="clipboard-document-2" :height="120" :width="120" dir="documents" />
				</div>
			</template>
		</shared-empty-state>

		<ul v-else>
			<li
				v-for="(item, index) in todos"
				:key="index"
				class="group relative flex cursor-pointer items-center gap-x-3 rounded px-3 py-5 transition hover:bg-gray-100"
				@click="item?.callback(item)"
			>
				<div
					class="avatar font-bold uppercase"
					:class="[
						{
							'before:bg-error-500': item?.status !== 'ongoing',
							'before:bg-gray-300': item?.status === 'ongoing'
						},
						item?.bg
					]"
				>
					<lazy-tools-icon
						:dir="item?.icon.dir || ''"
						:icon="item?.icon.name || ''"
						:path-class="item?.icon.pathClass || ''"
					/>
				</div>

				<div class="w-full flex-1 space-y-1">
					<div class="flex items-center justify-between">
						<h4 class="text-sm font-semibold capitalize text-gray-600">
							{{ item?.title }}
						</h4>
						<span class="text-xs text-gray-500">{{ timeDifference(new Date(item.created_at)) }}</span>
						<!-- <div
                            class="absolute bottom-2 right-2 w-[30px] group-hover:opacity-100 transition-all group/check hover:bg-primary-2  opacity-0 h-[30px] rounded bg-gray-200 flex items-center justify-center"
                            @click.stop="dataRep[item?.title]?.markAsDone(item)"
                            >
                            <lazy-tools-icon
                            dir="arrows"
                            icon="check"
                            path-class="fill-gray-400 group-hover/check:!fill-white"
                            />
                            </div> -->
					</div>

					<p
						class="w-full max-w-[320px] overflow-hidden text-ellipsis text-xs capitalize text-gray-500 sm:whitespace-nowrap"
					>
						{{ item?.text || '---' }}
					</p>
					<shared-badge
						:text="item.status"
						:pending="item.status === 'pending'"
						:ongoing="item.status === 'ongoing'"
						class="!text-xs"
					/>
				</div>
			</li>
		</ul>
		<dialogs-options
			v-model:show="isCustodianOptionOpen"
			:category-options="dialogOptions.data"
			:title="dialogOptions.title"
			:paragraph="dialogOptions.label"
			@cancel="() => (highlightedTodo = null)"
			@proceed="processTodo"
		/>
	</div>
</template>

<style scoped>
	.avatar {
		@apply relative flex h-[47px] w-[47px] flex-none items-center justify-center rounded-full text-white before:absolute before:-right-0 before:-top-1 before:h-[17px] before:w-[17px] before:rounded-full before:border-2 before:border-white;
	}
</style>
